// ── Demo mock data ──────────────────────────────────────────────
// Self-contained demo fixtures. Every user-facing string is stored as a
// translation triple (tr/en/nl) and resolved at runtime with buildDemoData(),
// so the demo follows the selected UI language.
// Conforms to production API types (../api/types.ts).

import type { Language } from '../i18n'
import type {
  SessionSummary,
  SessionDetail,
  SessionGrades,
  SessionAnalysis,
  ClassSummary,
  StudentSummary,
  OrgMember,
  DemoSwitchUser,
  CreditWallet,
  CreditTransaction,
  CostBySessionModelRow,
  Question,
  Paper,
  GradingRun,
} from '../api/types'

/** A string translated into every supported UI language. */
export type DemoText = { tr: string; en: string; nl: string }
const l = (tr: string, en: string, nl: string): DemoText => ({ tr, en, nl })

/** Question with the reference answer the grading panel can show. */
export type DemoQuestion = Question & { reference_answer?: string }

export interface DemoData {
  users: DemoSwitchUser[]
  classes: ClassSummary[]
  studentsByClass: Record<number, StudentSummary[]>
  exams: SessionSummary[]
  examDetails: Record<number, Omit<SessionDetail, 'questions'> & { questions: DemoQuestion[] }>
  examGrades: Record<number, SessionGrades>
  examAnalysis: Record<number, SessionAnalysis>
  members: OrgMember[]
  wallet: CreditWallet
  transactions: CreditTransaction[]
  costRows: CostBySessionModelRow[]
}

/** Maps every string of T to `string | DemoText`; other values pass through. */
type Loc<T> = T extends string
  ? string | DemoText
  : T extends readonly (infer U)[]
    ? Loc<U>[]
    : T extends object
      ? { [K in keyof T]: Loc<T[K]> }
      : T

// ── Localised content ───────────────────────────────────────────

const content: Loc<DemoData> = {
  // ── Demo users ────────────────────────────────────────────────
  users: [
    {
      id: 1,
      full_name: l('Kemal Yıldırım', 'James Carter', 'Koen de Vries'),
      email: l('kemal@admin.ai-grader.com', 'james@admin.gradescript.com', 'koen@admin.gradescript.com'),
      user_role: 'ADMIN',
      subscriber_id: 1,
      organization_name: l('Yıldız Koleji', 'Northfield Academy', 'Sint Lucas College'),
      subscriber_type: 'INSTITUTION',
      membership_role: 'ADMIN',
    },
    {
      id: 2,
      full_name: l('Ayşe Yılmaz', 'Sarah Mitchell', 'Sanne Jansen'),
      email: l('ayse@yildizkoleji.edu.tr', 'sarah@northfield.edu', 'sanne@sintlucas.nl'),
      user_role: 'ORG_MANAGER',
      subscriber_id: 1,
      organization_name: l('Yıldız Koleji', 'Northfield Academy', 'Sint Lucas College'),
      subscriber_type: 'INSTITUTION',
      membership_role: 'ORG_MANAGER',
    },
    {
      id: 3,
      full_name: l('Mehmet Demir', 'Daniel Foster', 'Daan Bakker'),
      email: l('mehmet@yildizkoleji.edu.tr', 'daniel@northfield.edu', 'daan@sintlucas.nl'),
      user_role: 'TEACHER',
      subscriber_id: 1,
      organization_name: l('Yıldız Koleji', 'Northfield Academy', 'Sint Lucas College'),
      subscriber_type: 'INSTITUTION',
      membership_role: 'TEACHER',
    },
  ],

  // ── Classes ───────────────────────────────────────────────────
  classes: [
    { id: 1, name: '8-A', subscriber_id: 1, lead_teacher_user_id: 2, created_at: '2025-09-01' },
    { id: 2, name: '9-A', subscriber_id: 1, lead_teacher_user_id: 2, created_at: '2025-09-01' },
    { id: 3, name: '9-B', subscriber_id: 1, lead_teacher_user_id: 3, created_at: '2025-09-01' },
    { id: 4, name: '10-A', subscriber_id: 1, lead_teacher_user_id: null, created_at: '2025-09-01' },
    { id: 5, name: '7-C', subscriber_id: 1, lead_teacher_user_id: null, created_at: '2025-09-01' },
    { id: 6, name: '8-B', subscriber_id: 1, lead_teacher_user_id: null, created_at: '2025-09-01' },
  ],

  // ── Students (keyed by class id) ──────────────────────────────
  studentsByClass: {
    1: [
      { id: 1, full_name: l('Ali Yıldız', 'Oliver Bennett', 'Emma Bakker'), student_no: '20260001', subscriber_id: 1 },
      { id: 2, full_name: l('Zeynep Arslan', 'Emily Clarke', 'Sem de Boer'), student_no: '20260002', subscriber_id: 1 },
      { id: 3, full_name: l('Burak Şahin', 'Jack Turner', 'Lucas Smit'), student_no: '20260003', subscriber_id: 1 },
      { id: 4, full_name: l('Selin Doğan', 'Sophie Hughes', 'Julia Meijer'), student_no: '20260004', subscriber_id: 1 },
      { id: 5, full_name: l('Emre Koç', 'Harry Wilson', 'Bram Mulder'), student_no: '20260005', subscriber_id: 1 },
      { id: 6, full_name: l('Defne Aydın', 'Grace Mitchell', 'Anouk Bos'), student_no: '20260006', subscriber_id: 1 },
      { id: 7, full_name: l('Can Özdemir', 'Charlie Adams', 'Thijs de Groot'), student_no: '20260007', subscriber_id: 1 },
    ],
    2: [
      { id: 8, full_name: l('Ece Tekin', 'Amelia Brooks', 'Eva Dijkstra'), student_no: '20260008', subscriber_id: 1 },
      { id: 9, full_name: l('Barış Polat', 'Thomas Reed', 'Niels Hofman'), student_no: '20260009', subscriber_id: 1 },
      { id: 10, full_name: l('Deniz Aksoy', 'Isla Morgan', 'Fleur Kuipers'), student_no: '20260010', subscriber_id: 1 },
    ],
    3: [
      { id: 11, full_name: l('Yusuf Karaca', 'Noah Foster', 'Ruben Vermeulen'), student_no: '20260011', subscriber_id: 1 },
      { id: 12, full_name: l('Irmak Şentürk', 'Isla Morgan', 'Lotte van Leeuwen'), student_no: '20260012', subscriber_id: 1 },
    ],
    4: [
      { id: 13, full_name: l('Kerem Yıldırım', 'Leo Grant', 'Stijn Bosch'), student_no: '20260013', subscriber_id: 1 },
      { id: 14, full_name: l('Melis Korkmaz', 'Ava Palmer', 'Maud Hendriks'), student_no: '20260014', subscriber_id: 1 },
    ],
    5: [
      { id: 15, full_name: l('Arda Kaya', 'Oscar Hughes', 'Joris Dekker'), student_no: '20260015', subscriber_id: 1 },
      { id: 16, full_name: l('Nil Çelik', 'Lily Barnes', 'Noor van Dam'), student_no: '20260016', subscriber_id: 1 },
    ],
    6: [
      { id: 17, full_name: l('Çağrı Öztürk', 'Max Cooper', 'Cas van Dijk'), student_no: '20260017', subscriber_id: 1 },
      { id: 18, full_name: l('Elif Acar', 'Ella Ward', 'Isa Prins'), student_no: '20260018', subscriber_id: 1 },
      { id: 19, full_name: l('Murat Tan', 'Finn Bailey', 'Tim Achterberg'), student_no: '20260019', subscriber_id: 1 },
      { id: 20, full_name: l('Hande Güler', 'Chloe Price', 'Sara Wolters'), student_no: '20260020', subscriber_id: 1 },
    ],
  },

  // ── Exams (summary list) ──────────────────────────────────────
  exams: [
    {
      id: 1,
      session_name: l('Ara Sınav — Cebir', 'Midterm — Algebra', 'Proefwerk — Algebra'),
      test_date: '2026-02-15',
      subscriber_id: 1,
      class_id: 1,
      created_at: '2026-02-10',
    },
    {
      id: 2,
      session_name: l('Dönemlik Fizik Sınavı', 'Term Physics Exam', 'Proefwerk Natuurkunde'),
      test_date: '2026-02-20',
      subscriber_id: 1,
      class_id: 3,
      created_at: '2026-02-14',
    },
    {
      id: 3,
      session_name: l('Kimya Lab Raporu', 'Chemistry Lab Report', 'Verslag Scheikunde Practicum'),
      test_date: '2026-03-01',
      subscriber_id: 1,
      class_id: 4,
      created_at: '2026-02-25',
    },
    {
      id: 4,
      session_name: l('Biyoloji Quiz #3', 'Biology Quiz #3', 'Biologie Quiz #3'),
      test_date: '2026-03-05',
      subscriber_id: 1,
      class_id: 5,
      created_at: '2026-03-01',
    },
  ],

  // ── Exam detail (exam id -> full detail) ──────────────────────
  examDetails: {
    1: {
      id: 1,
      session_name: l('Ara Sınav — Cebir', 'Midterm — Algebra', 'Proefwerk — Algebra'),
      test_date: '2026-02-15',
      subscriber_id: 1,
      class_id: 1,
      session_cost: 0.97,
      rubric_file_id: 'rubric-1',
      rubric_preview: {
        summary: l(
          'Matematik 8. sınıf ara sınav rubriği. 7 soru, toplam 100 puan.',
          'Grade 8 maths midterm rubric. 7 questions, 100 points in total.',
          'Rubriek proefwerk wiskunde klas 8. 7 vragen, 100 punten in totaal.',
        ),
        criteria: [
          {
            name: l('Çözüm Yöntemi', 'Solution Method', 'Oplossingsmethode'),
            expectation: l(
              'Doğru yöntem ve adımlar gösterilmeli',
              'Correct method and steps must be shown',
              'Juiste methode en stappen moeten zichtbaar zijn',
            ),
            weight_or_points: '40%',
          },
          {
            name: l('Hesaplama Doğruluğu', 'Calculation Accuracy', 'Rekennauwkeurigheid'),
            expectation: l(
              'Aritmetik işlemler hatasız olmalı',
              'Arithmetic must be error-free',
              'Rekenkundige bewerkingen moeten foutloos zijn',
            ),
            weight_or_points: '35%',
          },
          {
            name: l('Gösterim', 'Presentation', 'Presentatie'),
            expectation: l(
              'Temiz ve anlaşılır çözüm adımları',
              'Clean and legible solution steps',
              'Nette en leesbare uitwerking',
            ),
            weight_or_points: '25%',
          },
        ],
      },
      feedback_language: l('Turkish', 'English', 'Dutch'),
      max_points: 100,
      questions_approved: true,
      latest_run: {
        id: 1,
        session_id: 1,
        rubric_file_id: 'rubric-1',
        rubric_filename: l('matematik_rubrik.pdf', 'math_rubric.pdf', 'wiskunde_rubriek.pdf'),
        strictness: 'moderate',
        feedback_language: l('Turkish', 'English', 'Dutch'),
        created_at: '2026-02-15T12:00:00',
      },
      runs: [
        {
          id: 1,
          session_id: 1,
          rubric_file_id: 'rubric-1',
          rubric_filename: l('matematik_rubrik.pdf', 'math_rubric.pdf', 'wiskunde_rubriek.pdf'),
          strictness: 'moderate',
          feedback_language: l('Turkish', 'English', 'Dutch'),
          created_at: '2026-02-15T12:00:00',
        },
      ],
      questions: [
        {
          id: 1,
          question_number: 1,
          question_text: l(
            'Doğrusal denklemi çözün: 3x + 7 = 22',
            'Solve the linear equation: 3x + 7 = 22',
            'Los de lineaire vergelijking op: 3x + 7 = 22',
          ),
          max_points: 10,
          confidence_level: 0.95,
          reference_answer: 'x = 5',
        },
        {
          id: 2,
          question_number: 2,
          question_text: l(
            '360 sayısının asal çarpanlarını bulun',
            'Find the prime factors of 360',
            'Bepaal de priemfactoren van 360',
          ),
          max_points: 10,
          confidence_level: 0.92,
          reference_answer: '360 = 2³ · 3² · 5',
        },
        {
          id: 3,
          question_number: 3,
          question_text: l(
            'Üçgendeki eksik açıyı hesaplayın',
            'Calculate the missing angle in the triangle',
            'Bereken de ontbrekende hoek in de driehoek',
          ),
          max_points: 15,
          confidence_level: 0.88,
          reference_answer: l('45°', '45°', '45°'),
        },
        {
          id: 4,
          question_number: 4,
          question_text: l(
            'Bir tren 60 km/sa hızla 2.5 saat, ardından 80 km/sa hızla 1.5 saat gidiyor. Tüm yolculuğun ortalama hızı nedir?',
            'A train travels at 60 km/h for 2.5 hours, then at 80 km/h for 1.5 hours. What is the average speed for the whole journey?',
            'Een trein rijdt 2,5 uur met 60 km/u en daarna 1,5 uur met 80 km/u. Wat is de gemiddelde snelheid over de hele reis?',
          ),
          max_points: 20,
          confidence_level: 0.75,
          reference_answer: l('67,5 km/sa', '67.5 km/h', '67,5 km/u'),
        },
        {
          id: 5,
          question_number: 5,
          question_text: l(
            'Sadeleştirin: (3/4) ÷ (2/5) + 1.25',
            'Simplify: (3/4) ÷ (2/5) + 1.25',
            'Vereenvoudig: (3/4) ÷ (2/5) + 1.25',
          ),
          max_points: 15,
          confidence_level: 0.8,
          reference_answer: l('25/8 = 3,125', '25/8 = 3.125', '25/8 = 3,125'),
        },
        {
          id: 6,
          question_number: 6,
          question_text: l(
            'Çubuk grafiği yorumlayarak ortanca değeri bulun',
            'Interpret the bar chart and find the median',
            'Lees de staafdiagram af en bepaal de mediaan',
          ),
          max_points: 15,
          confidence_level: 0.82,
          reference_answer: l('Ortanca = 12', 'Median = 12', 'Mediaan = 12'),
        },
        {
          id: 7,
          question_number: 7,
          question_text: l(
            'Eşitsizliği çözün: 2x - 5 > 3x + 1',
            'Solve the inequality: 2x - 5 > 3x + 1',
            'Los de ongelijkheid op: 2x - 5 > 3x + 1',
          ),
          max_points: 15,
          confidence_level: 0.9,
          reference_answer: 'x < −6',
        },
      ],
      papers: [
        {
          id: 1,
          student_name: l('Ali Yıldız', 'Oliver Bennett', 'Emma Bakker'),
          student_id: 1,
          file_id: 'f1',
          original_filename: l('ali_yildiz_sinav.pdf', 'oliver_bennett_midterm.pdf', 'emma_bakker_proefwerk.pdf'),
          upload_timestamp: '2026-02-15T10:00:00',
          session_id: 1,
        },
        {
          id: 2,
          student_name: l('Zeynep Arslan', 'Emily Clarke', 'Sem de Boer'),
          student_id: 2,
          file_id: 'f2',
          original_filename: l('zeynep_arslan_sinav.pdf', 'emily_clarke_midterm.pdf', 'sem_de_boer_proefwerk.pdf'),
          upload_timestamp: '2026-02-15T10:01:00',
          session_id: 1,
        },
        {
          id: 3,
          student_name: l('Burak Şahin', 'Jack Turner', 'Lucas Smit'),
          student_id: 3,
          file_id: 'f3',
          original_filename: l('burak_sahin_sinav.pdf', 'jack_turner_midterm.pdf', 'lucas_smit_proefwerk.pdf'),
          upload_timestamp: '2026-02-15T10:02:00',
          session_id: 1,
        },
        {
          id: 4,
          student_name: l('Selin Doğan', 'Sophie Hughes', 'Julia Meijer'),
          student_id: 4,
          file_id: 'f4',
          original_filename: l('selin_dogan_sinav.pdf', 'sophie_hughes_midterm.pdf', 'julia_meijer_proefwerk.pdf'),
          upload_timestamp: '2026-02-15T10:03:00',
          session_id: 1,
        },
        {
          id: 5,
          student_name: l('Emre Koç', 'Harry Wilson', 'Bram Mulder'),
          student_id: 5,
          file_id: 'f5',
          original_filename: l('emre_koc_sinav.pdf', 'harry_wilson_midterm.pdf', 'bram_mulder_proefwerk.pdf'),
          upload_timestamp: '2026-02-15T10:04:00',
          session_id: 1,
        },
        {
          id: 6,
          student_name: l('Defne Aydın', 'Grace Mitchell', 'Anouk Bos'),
          student_id: 6,
          file_id: 'f6',
          original_filename: l('defne_aydin_sinav.pdf', 'grace_mitchell_midterm.pdf', 'anouk_bos_proefwerk.pdf'),
          upload_timestamp: '2026-02-15T10:05:00',
          session_id: 1,
        },
        {
          id: 7,
          student_name: l('Can Özdemir', 'Charlie Adams', 'Thijs de Groot'),
          student_id: 7,
          file_id: 'f7',
          original_filename: l('can_ozdemir_sinav.pdf', 'charlie_adams_midterm.pdf', 'thijs_de_groot_proefwerk.pdf'),
          upload_timestamp: '2026-02-15T10:06:00',
          session_id: 1,
        },
      ],
    },
    2: {
      id: 2,
      session_name: l('Dönemlik Fizik Sınavı', 'Term Physics Exam', 'Proefwerk Natuurkunde'),
      test_date: '2026-02-20',
      subscriber_id: 1,
      class_id: 3,
      session_cost: 0.78,
      rubric_file_id: 'rubric-2',
      rubric_preview: null,
      feedback_language: l('Turkish', 'English', 'Dutch'),
      max_points: 80,
      questions_approved: true,
      latest_run: {
        id: 2,
        session_id: 2,
        strictness: 'moderate',
        feedback_language: l('Turkish', 'English', 'Dutch'),
        created_at: '2026-02-20T14:00:00',
      },
      runs: [
        {
          id: 2,
          session_id: 2,
          strictness: 'moderate',
          feedback_language: l('Turkish', 'English', 'Dutch'),
          created_at: '2026-02-20T14:00:00',
        },
      ],
      questions: [
        {
          id: 10,
          question_number: 1,
          question_text: l(
            "Newton'un ikinci yasasını tanımlayınız ve bir örnek veriniz",
            "State Newton's second law and give an example",
            'Formuleer de tweede wet van Newton en geef een voorbeeld',
          ),
          max_points: 20,
          confidence_level: 0.9,
          reference_answer: l(
            'F = m·a; örnek: alışveriş arabası',
            'F = m·a; example: a shopping trolley',
            'F = m·a; voorbeeld: een boodschappenkar',
          ),
        },
        {
          id: 11,
          question_number: 2,
          question_text: l(
            'Kinematik denklemlerle hız-zaman grafiğini analiz ediniz',
            'Analyse the velocity–time graph using kinematic equations',
            'Analyseer de snelheid-tijdgrafiek met de kinematica-vergelijkingen',
          ),
          max_points: 20,
          confidence_level: 0.85,
          reference_answer: l(
            'Eğim ivmeyi verir; alan yolu verir',
            'Slope gives acceleration; area gives distance',
            'Helling geeft versnelling; oppervlakte geeft afstand',
          ),
        },
        {
          id: 12,
          question_number: 3,
          question_text: l(
            'Dalga hareketinde frekans ve dalga boyu ilişkisini açıklayınız',
            'Explain the relationship between frequency and wavelength in wave motion',
            'Leg het verband uit tussen frequentie en golflengte bij golfbeweging',
          ),
          max_points: 20,
          confidence_level: 0.8,
          reference_answer: 'v = f · λ',
        },
        {
          id: 13,
          question_number: 4,
          question_text: l(
            'Enerji koruması ilkesiyle verilen problemi çözünüz',
            'Solve the problem using the law of conservation of energy',
            'Los het vraagstuk op met de wet van behoud van energie',
          ),
          max_points: 20,
          confidence_level: 0.82,
          reference_answer: l(
            'E_potansiyel = E_kinetik',
            'E_potential = E_kinetic',
            'E_potentieel = E_kinetisch',
          ),
        },
      ],
      papers: [
        {
          id: 10,
          student_name: l('Yusuf Karaca', 'Noah Foster', 'Ruben Vermeulen'),
          student_id: 11,
          file_id: 'f10',
          original_filename: l('yusuf_fizik.pdf', 'noah_foster_physics.pdf', 'ruben_vermeulen_natuurkunde.pdf'),
          upload_timestamp: '2026-02-20T10:00:00',
          session_id: 2,
        },
        {
          id: 11,
          student_name: l('Irmak Şentürk', 'Isla Morgan', 'Lotte van Leeuwen'),
          student_id: 12,
          file_id: 'f11',
          original_filename: l('irmak_fizik.pdf', 'isla_morgan_physics.pdf', 'lotte_van_leeuwen_natuurkunde.pdf'),
          upload_timestamp: '2026-02-20T10:01:00',
          session_id: 2,
        },
      ],
    },
    3: {
      id: 3,
      session_name: l('Kimya Lab Raporu', 'Chemistry Lab Report', 'Verslag Scheikunde Practicum'),
      test_date: '2026-03-01',
      subscriber_id: 1,
      class_id: 4,
      session_cost: 0,
      rubric_file_id: null,
      rubric_preview: null,
      feedback_language: l('Turkish', 'English', 'Dutch'),
      max_points: 50,
      latest_run: null,
      runs: [],
      questions: [],
      papers: [],
    },
    4: {
      id: 4,
      session_name: l('Biyoloji Quiz #3', 'Biology Quiz #3', 'Biologie Quiz #3'),
      test_date: '2026-03-05',
      subscriber_id: 1,
      class_id: 5,
      session_cost: 0,
      rubric_file_id: null,
      rubric_preview: null,
      feedback_language: l('Turkish', 'English', 'Dutch'),
      max_points: 30,
      latest_run: null,
      runs: [],
      questions: [],
      papers: [],
    },
  },

  // ── Exam grades (exam id -> grades) ───────────────────────────
  examGrades: {
    1: [
      {
        paper_id: 1,
        student_name: l('Ali Yıldız', 'Oliver Bennett', 'Emma Bakker'),
        original_filename: l('ali_yildiz_sinav.pdf', 'oliver_bennett_midterm.pdf', 'emma_bakker_proefwerk.pdf'),
        upload_timestamp: '2026-02-15T10:00:00',
        file_id: 'f1',
        paper_grade: {
          total_score: 82,
          status: 'completed',
          overall_feedback: l(
            'Güçlü cebirsel beceriler gösterdi. Oran hesaplamalarını içeren sözel problemlerde gelişme gerekli. Çok adımlı çevirme problemleri pratik yapılmalı.',
            'Showed strong algebraic skills. Needs improvement on word problems involving ratio calculations. Multi-step conversion problems should be practised.',
            'Sterke algebraïsche vaardigheden. Verbetering nodig bij verhaalsommen met verhoudingen. Oefen meerstaps omrekenopgaven.',
          ),
          graded_at: '2026-02-15T12:05:00',
        },
        question_grades: [
          {
            id: 1,
            question_number: 1,
            question_display: 'S1',
            score: 9,
            max_score: 10,
            feedback: l(
              'Doğru çözüm, açık işlem adımları gösterilmiş.',
              'Correct solution with clear working steps.',
              'Juiste oplossing met duidelijke tussenstappen.',
            ),
            student_answer: '3x = 15 → x = 5',
          },
          {
            id: 2,
            question_number: 2,
            question_display: 'S2',
            score: 10,
            max_score: 10,
            feedback: l(
              'Mükemmel asal çarpanlara ayırma, faktör ağacı yöntemi kullanılmış.',
              'Excellent prime factorisation using the factor tree method.',
              'Uitstekende priemontbinding met de factorenboom.',
            ),
            student_answer: '360 = 2³ · 3² · 5',
          },
          {
            id: 3,
            question_number: 3,
            question_display: 'S3',
            score: 14,
            max_score: 15,
            feedback: l(
              'Doğru cevap. Açı etiketlemesinde küçük notasyon hatası.',
              'Correct answer. Minor notation slip in the angle labelling.',
              'Juist antwoord. Kleine notatiefout bij het benoemen van de hoeken.',
            ),
            student_answer: '180° − 135° = 45°',
          },
          {
            id: 4,
            question_number: 4,
            question_display: 'S4',
            score: 12,
            max_score: 20,
            feedback: l(
              'Denklemi doğru kurmuş ama son adımda aritmetik hatası var. Mesafe formülü doğru uygulanmış.',
              'Set up the equation correctly but made an arithmetic error in the final step. Distance formula applied correctly.',
              'Vergelijking goed opgesteld, maar rekenfout in de laatste stap. Afstandsformule correct toegepast.',
            ),
            student_answer: 'v = (150 + 120) / 4 = 67,5',
          },
          {
            id: 5,
            question_number: 5,
            question_display: 'S5',
            score: 11,
            max_score: 15,
            feedback: l(
              'Kesir bölmesi doğru, ancak ondalık çevirme yuvarlamada hata.',
              'Fraction division is correct, but there is a rounding error in the decimal conversion.',
              'Breukdeling klopt, maar afrondingsfout bij de omzetting naar decimalen.',
            ),
            student_answer: '15/8 + 5/4 = 25/8 = 3,125',
          },
          {
            id: 6,
            question_number: 6,
            question_display: 'S6',
            score: 12,
            max_score: 15,
            feedback: l(
              'Çubuk grafiği okuması isabetli. Ortanca hesaplaması doğru.',
              'Accurate reading of the bar chart. Median calculated correctly.',
              'Staafdiagram correct afgelezen. Mediaan juist berekend.',
            ),
            student_answer: '3, 5, 8, 12, 14 → 12',
          },
          {
            id: 7,
            question_number: 7,
            question_display: 'S7',
            score: 14,
            max_score: 15,
            feedback: l(
              'Eşitsizlik doğru yönde çözülmüş.',
              'Inequality solved in the right direction.',
              'Ongelijkheid in de juiste richting opgelost.',
            ),
            student_answer: 'x < −6',
          },
        ],
      },
      {
        paper_id: 2,
        student_name: l('Zeynep Arslan', 'Emily Clarke', 'Sem de Boer'),
        original_filename: l('zeynep_arslan_sinav.pdf', 'emily_clarke_midterm.pdf', 'sem_de_boer_proefwerk.pdf'),
        upload_timestamp: '2026-02-15T10:01:00',
        file_id: 'f2',
        paper_grade: {
          total_score: 91,
          status: 'completed',
          overall_feedback: l(
            'Tüm konularda mükemmel performans. Güçlü matematiksel mantık yürütme ve açık çözüm sunumu gösteriyor.',
            'Excellent performance across all topics. Strong mathematical reasoning and clear presentation of solutions.',
            'Uitstekende prestaties op alle onderdelen. Sterk wiskundig redeneren en duidelijke uitwerking.',
          ),
          graded_at: '2026-02-15T12:06:00',
        },
        question_grades: [
          {
            id: 8,
            question_number: 1,
            question_display: 'S1',
            score: 10,
            max_score: 10,
            feedback: l('Mükemmel çözüm.', 'Excellent solution.', 'Uitstekende oplossing.'),
            student_answer: 'x = 5',
          },
          {
            id: 9,
            question_number: 2,
            question_display: 'S2',
            score: 9,
            max_score: 10,
            feedback: l(
              'Doğru çarpanlara ayırma, ara adım eksik.',
              'Correct factorisation, intermediate step missing.',
              'Correcte ontbinding, tussenstap ontbreekt.',
            ),
            student_answer: '2³ · 3² · 5',
          },
          {
            id: 10,
            question_number: 3,
            question_display: 'S3',
            score: 15,
            max_score: 15,
            feedback: l(
              'Kusursuz uygulama, doğru geometrik notasyon.',
              'Flawless working with correct geometric notation.',
              'Foutloze uitwerking met correcte geometrische notatie.',
            ),
            student_answer: '45°',
          },
          {
            id: 11,
            question_number: 4,
            question_display: 'S4',
            score: 16,
            max_score: 20,
            feedback: l(
              'İyi problem kurulumu. Sonda küçük birim çevirme hatası.',
              'Good problem setup. Minor unit conversion error at the end.',
              'Goede opzet. Kleine fout in de eenheidsomzetting aan het eind.',
            ),
            student_answer: 'v = 270 / 4 = 67,5',
          },
          {
            id: 12,
            question_number: 5,
            question_display: 'S5',
            score: 13,
            max_score: 15,
            feedback: l(
              'Başarılı. Temiz kesir işlemleri.',
              'Well done. Clean fraction work.',
              'Goed gedaan. Nette breukberekeningen.',
            ),
            student_answer: '25/8',
          },
          {
            id: 13,
            question_number: 6,
            question_display: 'S6',
            score: 14,
            max_score: 15,
            feedback: l(
              'Mükemmel grafik yorumlama. Ortalamada küçük aritmetik kayma.',
              'Excellent chart interpretation. Small arithmetic slip in the mean.',
              'Uitstekende grafiekinterpretatie. Kleine rekenfout in het gemiddelde.',
            ),
            student_answer: '12',
          },
          {
            id: 14,
            question_number: 7,
            question_display: 'S7',
            score: 14,
            max_score: 15,
            feedback: l(
              'Doğru eşitsizlik, açık adımları ile.',
              'Correct inequality with clear steps.',
              'Correcte ongelijkheid met duidelijke stappen.',
            ),
            student_answer: 'x < −6',
          },
        ],
      },
      {
        paper_id: 3,
        student_name: l('Burak Şahin', 'Jack Turner', 'Lucas Smit'),
        original_filename: l('burak_sahin_sinav.pdf', 'jack_turner_midterm.pdf', 'lucas_smit_proefwerk.pdf'),
        upload_timestamp: '2026-02-15T10:02:00',
        file_id: 'f3',
        paper_grade: {
          total_score: 68,
          status: 'completed',
          overall_feedback: l(
            'Temel kavramları anlıyor ancak çok adımlı problemlerde zorlaşıyor. Sözel problem ve kesir işlemleri için ek pratik önerilir.',
            'Understands the basic concepts but struggles with multi-step problems. Extra practice on word problems and fractions is recommended.',
            'Begrijpt de basisbegrippen, maar heeft moeite met meerstaps opgaven. Extra oefening met verhaalsommen en breuken aanbevolen.',
          ),
          graded_at: '2026-02-15T12:07:00',
        },
        question_grades: [
          {
            id: 15,
            question_number: 1,
            question_display: 'S1',
            score: 8,
            max_score: 10,
            feedback: l(
              'Doğru yaklaşım ama cebirsel işlemde işaret hatası.',
              'Right approach but a sign error in the algebra.',
              'Juiste aanpak, maar tekenfout in de algebra.',
            ),
            student_answer: '3x = 15 → x = −5',
          },
          {
            id: 16,
            question_number: 2,
            question_display: 'S2',
            score: 7,
            max_score: 10,
            feedback: l(
              'Eksik çarpanlara ayırma — 2 faktörü atlanmış.',
              'Incomplete factorisation — the factor 2 was missed.',
              'Onvolledige ontbinding — factor 2 vergeten.',
            ),
            student_answer: '360 = 2² · 3² · 5',
          },
          {
            id: 17,
            question_number: 3,
            question_display: 'S3',
            score: 12,
            max_score: 15,
            feedback: l(
              'Doğru açıyı bulmuş ama gerekçe belirsiz.',
              'Found the right angle but the reasoning is unclear.',
              'Juiste hoek gevonden, maar de onderbouwing is onduidelijk.',
            ),
            student_answer: '45°',
          },
          {
            id: 18,
            question_number: 4,
            question_display: 'S4',
            score: 8,
            max_score: 20,
            feedback: l(
              'Sözel problemden denklem kurmakta zorlanmış. Değişkenleri doğru belirlemesi için kısmi puan.',
              'Struggled to turn the word problem into an equation. Partial credit for identifying the variables.',
              'Moeite met het opstellen van de vergelijking. Deelpunt voor het juist benoemen van de variabelen.',
            ),
            student_answer: '(60 + 80) / 2 = 70',
          },
          {
            id: 19,
            question_number: 5,
            question_display: 'S5',
            score: 9,
            max_score: 15,
            feedback: l(
              'Kesir bölmesinde hata. Yanlış kesri ters çevirmiş.',
              'Error in fraction division — the wrong fraction was inverted.',
              'Fout bij breukdeling — de verkeerde breuk omgedraaid.',
            ),
            student_answer: '8/15 + 1,25',
          },
          {
            id: 20,
            question_number: 6,
            question_display: 'S6',
            score: 10,
            max_score: 15,
            feedback: l(
              'Grafiği doğru okumuş ama yanlış ortanca hesaplamış.',
              'Read the chart correctly but calculated the median incorrectly.',
              'Grafiek juist afgelezen, maar mediaan onjuist berekend.',
            ),
            student_answer: '11',
          },
          {
            id: 21,
            question_number: 7,
            question_display: 'S7',
            score: 14,
            max_score: 15,
            feedback: l(
              'Eşitsizlikte iyi cebirsel işlem.',
              'Good algebraic work on the inequality.',
              'Goede algebraïsche uitwerking van de ongelijkheid.',
            ),
            student_answer: 'x < −6',
          },
        ],
      },
      {
        paper_id: 4,
        student_name: l('Selin Doğan', 'Sophie Hughes', 'Julia Meijer'),
        original_filename: l('selin_dogan_sinav.pdf', 'sophie_hughes_midterm.pdf', 'julia_meijer_proefwerk.pdf'),
        upload_timestamp: '2026-02-15T10:03:00',
        file_id: 'f4',
        paper_grade: {
          total_score: 88,
          status: 'completed',
          overall_feedback: l(
            'Çok güçlü performans. Tüm konularda tutarlı doğruluk ve mükemmel problem çözme stratejileri göstermiştir.',
            'Very strong performance. Consistent accuracy across all topics and excellent problem-solving strategies.',
            'Zeer sterke prestatie. Consistent nauwkeurig op alle onderdelen en uitstekende probleemoplossingsstrategieën.',
          ),
          graded_at: '2026-02-15T12:08:00',
        },
        question_grades: [
          {
            id: 22,
            question_number: 1,
            question_display: 'S1',
            score: 10,
            max_score: 10,
            feedback: l('Mükemmel.', 'Excellent.', 'Uitstekend.'),
            student_answer: 'x = 5',
          },
          {
            id: 23,
            question_number: 2,
            question_display: 'S2',
            score: 10,
            max_score: 10,
            feedback: l(
              'Eksiksiz ve doğru çarpanlara ayırma.',
              'Complete and correct factorisation.',
              'Volledige en correcte ontbinding.',
            ),
            student_answer: '2³ · 3² · 5',
          },
          {
            id: 24,
            question_number: 3,
            question_display: 'S3',
            score: 13,
            max_score: 15,
            feedback: l(
              'Doğru cevap. Diyagram etiketlemesi iyileştirilebilir.',
              'Correct answer. Diagram labelling could be improved.',
              'Juist antwoord. De diagramlabels kunnen beter.',
            ),
            student_answer: '45°',
          },
          {
            id: 25,
            question_number: 4,
            question_display: 'S4',
            score: 14,
            max_score: 20,
            feedback: l(
              'Sözel probleme iyi yaklaşım. Son cevapda küçük hesaplama hatası.',
              'Good approach to the word problem. Small calculation error in the final answer.',
              'Goede aanpak van de verhaalsom. Kleine rekenfout in het eindantwoord.',
            ),
            student_answer: 'v = 269,6 / 4 = 67,4',
          },
          {
            id: 26,
            question_number: 5,
            question_display: 'S5',
            score: 13,
            max_score: 15,
            feedback: l(
              'Kesirler ve ondalıklarda temiz çalışma.',
              'Clean work with fractions and decimals.',
              'Nette uitwerking met breuken en decimalen.',
            ),
            student_answer: '3,125',
          },
          {
            id: 27,
            question_number: 6,
            question_display: 'S6',
            score: 13,
            max_score: 15,
            feedback: l(
              'İsabetli grafik okuma, doğru istatistiksel ölçümler.',
              'Accurate chart reading with correct statistical measures.',
              'Nauwkeurig grafiek lezen met juiste statistische maten.',
            ),
            student_answer: '12',
          },
          {
            id: 28,
            question_number: 7,
            question_display: 'S7',
            score: 15,
            max_score: 15,
            feedback: l(
              'Mükemmel eşitsizlik çözümü, sayı doğrusu gösterimi ile.',
              'Excellent inequality solution with a number-line representation.',
              'Uitstekende ongelijkheidsoplossing met getallenlijn.',
            ),
            student_answer: 'x < −6',
          },
        ],
      },
      {
        paper_id: 5,
        student_name: l('Emre Koç', 'Harry Wilson', 'Bram Mulder'),
        original_filename: l('emre_koc_sinav.pdf', 'harry_wilson_midterm.pdf', 'bram_mulder_proefwerk.pdf'),
        upload_timestamp: '2026-02-15T10:04:00',
        file_id: 'f5',
        paper_grade: {
          total_score: 73,
          status: 'completed',
          overall_feedback: l(
            'Sağlam temel beceriler. Sözel problemler ve veri yorumlama odaklı geliştirme alanları. Cebirsel mantık umut verici.',
            'Solid foundational skills. Word problems and data interpretation are areas to develop. Algebraic reasoning is promising.',
            'Solide basisvaardigheden. Verhaalsommen en data-interpretatie zijn ontwikkelpunten. Algebraïsch redeneren is veelbelovend.',
          ),
          graded_at: '2026-02-15T12:09:00',
        },
        question_grades: [
          {
            id: 29,
            question_number: 1,
            question_display: 'S1',
            score: 9,
            max_score: 10,
            feedback: l(
              'Neredeyse mükemmel — küçük aritmetik hata.',
              'Nearly perfect — minor arithmetic error.',
              'Bijna perfect — kleine rekenfout.',
            ),
            student_answer: '3x = 16 → x = 5,3',
          },
          {
            id: 30,
            question_number: 2,
            question_display: 'S2',
            score: 8,
            max_score: 10,
            feedback: l(
              'İyi çarpanlara ayırma tekniği.',
              'Good factorisation technique.',
              'Goede ontbindingstechniek.',
            ),
            student_answer: '2³ · 3² · 5',
          },
          {
            id: 31,
            question_number: 3,
            question_display: 'S3',
            score: 11,
            max_score: 15,
            feedback: l(
              'Doğru kavram ama eksik gerekçelendirme.',
              'Right concept but incomplete justification.',
              'Juist concept, maar onvolledige onderbouwing.',
            ),
            student_answer: '45°',
          },
          {
            id: 32,
            question_number: 4,
            question_display: 'S4',
            score: 10,
            max_score: 20,
            feedback: l(
              'Kısmi doğru kurulum. Eksik çözüm için puan kaybı.',
              'Partially correct setup. Points lost for an incomplete solution.',
              'Gedeeltelijk juiste opzet. Punten verloren door onvolledige uitwerking.',
            ),
            student_answer: 'v = 140 / 4 = 35',
          },
          {
            id: 33,
            question_number: 5,
            question_display: 'S5',
            score: 10,
            max_score: 15,
            feedback: l(
              'Kesir işlemleri çoğunlukla doğru.',
              'Fraction operations mostly correct.',
              'Breukbewerkingen grotendeels juist.',
            ),
            student_answer: '25/8',
          },
          {
            id: 34,
            question_number: 6,
            question_display: 'S6',
            score: 11,
            max_score: 15,
            feedback: l(
              'Grafik okuması fena değil ama ortanca formülü yanlış uygulanmış.',
              'Chart reading is decent, but the median formula was applied incorrectly.',
              'Grafiek lezen is redelijk, maar de mediaanformule is verkeerd toegepast.',
            ),
            student_answer: '12',
          },
          {
            id: 35,
            question_number: 7,
            question_display: 'S7',
            score: 14,
            max_score: 15,
            feedback: l(
              'Güçlü eşitsizlik çalışması.',
              'Strong work on the inequality.',
              'Sterke uitwerking van de ongelijkheid.',
            ),
            student_answer: 'x < −6',
          },
        ],
      },
      {
        paper_id: 6,
        student_name: l('Defne Aydın', 'Grace Mitchell', 'Anouk Bos'),
        original_filename: l('defne_aydin_sinav.pdf', 'grace_mitchell_midterm.pdf', 'anouk_bos_proefwerk.pdf'),
        upload_timestamp: '2026-02-15T10:05:00',
        file_id: 'f6',
        paper_grade: {
          total_score: 95,
          status: 'completed',
          overall_feedback: l(
            'Tüm alanlarda üstün çalışma. Sınıfın en iyi performanslarından biri. İleri matematiksel düşünme ve titiz sunum gösteriyor.',
            'Outstanding work in every area. One of the best performances in the class, with advanced mathematical thinking and meticulous presentation.',
            'Uitmuntend werk op alle onderdelen. Een van de beste prestaties van de klas, met geavanceerd wiskundig denken en zeer verzorgde uitwerking.',
          ),
          graded_at: '2026-02-15T12:10:00',
        },
        question_grades: [
          {
            id: 36,
            question_number: 1,
            question_display: 'S1',
            score: 10,
            max_score: 10,
            feedback: l('Mükemmel.', 'Excellent.', 'Uitstekend.'),
            student_answer: 'x = 5',
          },
          {
            id: 37,
            question_number: 2,
            question_display: 'S2',
            score: 10,
            max_score: 10,
            feedback: l(
              'Mükemmel ve kapsamlı çarpanlara ayırma.',
              'Excellent and thorough factorisation.',
              'Uitstekende en volledige ontbinding.',
            ),
            student_answer: '2³ · 3² · 5',
          },
          {
            id: 38,
            question_number: 3,
            question_display: 'S3',
            score: 15,
            max_score: 15,
            feedback: l(
              'Zarif çözüm yaklaşımıyla kusursuz.',
              'Flawless with an elegant solution approach.',
              'Foutloos met een elegante aanpak.',
            ),
            student_answer: '45°',
          },
          {
            id: 39,
            question_number: 4,
            question_display: 'S4',
            score: 18,
            max_score: 20,
            feedback: l(
              'Mükemmel sözel problem stratejisi. Küçük yuvarlama farkı.',
              'Excellent word-problem strategy. Minor rounding difference.',
              'Uitstekende strategie voor de verhaalsom. Kleine afrondingsverschil.',
            ),
            student_answer: 'v = 270 / 4 = 67,5',
          },
          {
            id: 40,
            question_number: 5,
            question_display: 'S5',
            score: 14,
            max_score: 15,
            feedback: l(
              'Neredeyse kusursuz kesir çalışması.',
              'Nearly flawless fraction work.',
              'Bijna foutloze breukberekening.',
            ),
            student_answer: '25/8 = 3,125',
          },
          {
            id: 41,
            question_number: 6,
            question_display: 'S6',
            score: 14,
            max_score: 15,
            feedback: l(
              'Kapsamlı grafik istatistiksel analizi.',
              'Thorough statistical analysis of the chart.',
              'Grondige statistische analyse van de grafiek.',
            ),
            student_answer: '12',
          },
          {
            id: 42,
            question_number: 7,
            question_display: 'S7',
            score: 14,
            max_score: 15,
            feedback: l(
              'Açık adım adım mantık yürütme ile doğru.',
              'Correct with clear step-by-step reasoning.',
              'Juist met duidelijke stapsgewijze redenering.',
            ),
            student_answer: 'x < −6',
          },
        ],
      },
      {
        paper_id: 7,
        student_name: l('Can Özdemir', 'Charlie Adams', 'Thijs de Groot'),
        original_filename: l('can_ozdemir_sinav.pdf', 'charlie_adams_midterm.pdf', 'thijs_de_groot_proefwerk.pdf'),
        upload_timestamp: '2026-02-15T10:06:00',
        file_id: 'f7',
        paper_grade: {
          total_score: 59,
          status: 'completed',
          overall_feedback: l(
            'Sınıf ortalamasının altında. Sözel problem anlama ve kesir işlemlerinde önemli boşluklar. Bu konularda birebir ders desteği önerilir.',
            'Below the class average. Significant gaps in understanding word problems and in fraction operations. One-to-one support in these topics is recommended.',
            'Onder het klassengemiddelde. Belangrijke hiaten bij verhaalsommen en breukbewerkingen. Een-op-een begeleiding wordt aanbevolen.',
          ),
          graded_at: '2026-02-15T12:11:00',
        },
        question_grades: [
          {
            id: 43,
            question_number: 1,
            question_display: 'S1',
            score: 7,
            max_score: 10,
            feedback: l(
              'Doğru yaklaşım ama birden fazla hesaplama hatası.',
              'Right approach but several calculation errors.',
              'Juiste aanpak, maar meerdere rekenfouten.',
            ),
            student_answer: '3x = 15 → x = 4',
          },
          {
            id: 44,
            question_number: 2,
            question_display: 'S2',
            score: 6,
            max_score: 10,
            feedback: l(
              'Hatalı eksik çarpanlara ayırma.',
              'Incorrect, incomplete factorisation.',
              'Onjuiste, onvolledige ontbinding.',
            ),
            student_answer: '360 = 2 · 3 · 5',
          },
          {
            id: 45,
            question_number: 3,
            question_display: 'S3',
            score: 10,
            max_score: 15,
            feedback: l(
              'Kavramı anladı ama uygulamada hatalar.',
              'Understood the concept but made errors in the working.',
              'Begreep het concept, maar maakte fouten in de uitwerking.',
            ),
            student_answer: '45°',
          },
          {
            id: 46,
            question_number: 4,
            question_display: 'S4',
            score: 6,
            max_score: 20,
            feedback: l(
              'Denklemi doğru kuramadı. Denemesi için kısmi puan.',
              'Could not set up the equation correctly. Partial credit for the attempt.',
              'Kon de vergelijking niet correct opstellen. Deelpunt voor de poging.',
            ),
            student_answer: 'v = 100 / 4',
          },
          {
            id: 47,
            question_number: 5,
            question_display: 'S5',
            score: 7,
            max_score: 15,
            feedback: l(
              'Kesir ve ondalık işlemlerinde birden fazla hata.',
              'Several errors in fraction and decimal operations.',
              'Meerdere fouten bij breuken en decimalen.',
            ),
            student_answer: '15/8',
          },
          {
            id: 48,
            question_number: 6,
            question_display: 'S6',
            score: 10,
            max_score: 15,
            feedback: l(
              'Temel grafik okuması iyi, hesaplamalarda zorlanmış.',
              'Basic chart reading is fine; struggled with the calculations.',
              'Basis grafiek lezen is goed; moeite met de berekeningen.',
            ),
            student_answer: '13',
          },
          {
            id: 49,
            question_number: 7,
            question_display: 'S7',
            score: 13,
            max_score: 15,
            feedback: l(
              'Önceki zorluklara rağmen iyi eşitsizlik işlemi.',
              'Good work on the inequality despite earlier difficulties.',
              'Goede uitwerking van de ongelijkheid ondanks eerdere problemen.',
            ),
            student_answer: 'x < −6',
          },
        ],
      },
    ],
    2: [
      {
        paper_id: 10,
        student_name: l('Yusuf Karaca', 'Noah Foster', 'Ruben Vermeulen'),
        original_filename: l('yusuf_fizik.pdf', 'noah_foster_physics.pdf', 'ruben_vermeulen_natuurkunde.pdf'),
        upload_timestamp: '2026-02-20T10:00:00',
        file_id: 'f10',
        paper_grade: {
          total_score: 62,
          status: 'completed',
          overall_feedback: l(
            'Newton yasalarında iyi kavrayış, dalga konularında geliştirme gerekli.',
            "Good understanding of Newton's laws; needs development on waves.",
            'Goed begrip van de wetten van Newton; ontwikkeling nodig bij golven.',
          ),
          graded_at: '2026-02-20T14:05:00',
        },
        question_grades: [
          {
            id: 50,
            question_number: 1,
            question_display: 'S1',
            score: 17,
            max_score: 20,
            feedback: l(
              'İyi tanımlama ve örnek.',
              'Good definition and example.',
              'Goede definitie en voorbeeld.',
            ),
          },
          {
            id: 51,
            question_number: 2,
            question_display: 'S2',
            score: 15,
            max_score: 20,
            feedback: l(
              'Grafik analizi doğru ama yorumlama eksik.',
              'Graph analysis is correct but interpretation is missing.',
              'Grafiekanalyse klopt, maar interpretatie ontbreekt.',
            ),
          },
          {
            id: 52,
            question_number: 3,
            question_display: 'S3',
            score: 14,
            max_score: 20,
            feedback: l(
              'Temel ilişki açıklanmış, formül kullanımı zayıf.',
              'Basic relationship explained; formula use is weak.',
              'Basisrelatie uitgelegd; formulegebruik is zwak.',
            ),
          },
          {
            id: 53,
            question_number: 4,
            question_display: 'S4',
            score: 16,
            max_score: 20,
            feedback: l(
              'Enerji koruması doğru uygulanmış.',
              'Conservation of energy applied correctly.',
              'Wet van behoud van energie correct toegepast.',
            ),
          },
        ],
      },
      {
        paper_id: 11,
        student_name: l('Irmak Şentürk', 'Isla Morgan', 'Lotte van Leeuwen'),
        original_filename: l('irmak_fizik.pdf', 'isla_morgan_physics.pdf', 'lotte_van_leeuwen_natuurkunde.pdf'),
        upload_timestamp: '2026-02-20T10:01:00',
        file_id: 'f11',
        paper_grade: {
          total_score: 70,
          status: 'completed',
          overall_feedback: l(
            'Genel olarak güçlü performans. Tüm konularda tutarlı başarı.',
            'Generally strong performance with consistent results across topics.',
            'Over het algemeen sterke prestatie met consistente resultaten op alle onderdelen.',
          ),
          graded_at: '2026-02-20T14:06:00',
        },
        question_grades: [
          {
            id: 54,
            question_number: 1,
            question_display: 'S1',
            score: 19,
            max_score: 20,
            feedback: l(
              'Kapsamlı tanımlama ve yaşam örneği.',
              'Thorough definition with a real-life example.',
              'Uitgebreide definitie met een voorbeeld uit het dagelijks leven.',
            ),
          },
          {
            id: 55,
            question_number: 2,
            question_display: 'S2',
            score: 17,
            max_score: 20,
            feedback: l(
              'Grafik analizi iyi, adımlar açık.',
              'Good graph analysis with clear steps.',
              'Goede grafiekanalyse met duidelijke stappen.',
            ),
          },
          {
            id: 56,
            question_number: 3,
            question_display: 'S3',
            score: 16,
            max_score: 20,
            feedback: l(
              'İlişkiyi iyi açıklamış, formülü doğru.',
              'Explained the relationship well and used the formula correctly.',
              'Relatie goed uitgelegd en formule correct gebruikt.',
            ),
          },
          {
            id: 57,
            question_number: 4,
            question_display: 'S4',
            score: 18,
            max_score: 20,
            feedback: l(
              'Çözüm yöntemi ve sonuç doğru.',
              'Method and result are correct.',
              'Methode en uitkomst zijn juist.',
            ),
          },
        ],
      },
    ],
  },

  // ── Exam analysis (exam id -> analysis) ───────────────────────
  examAnalysis: {
    1: {
      run_id: 1,
      paper_count: 7,
      class_average: { average_score: 79.4, max_score: 100, success_percent: 79.4 },
      feedback_language: l('Turkish', 'English', 'Dutch'),
      question_stats: [
        {
          question_number: 1,
          question_text: l(
            'Doğrusal denklemi çözün: 3x + 7 = 22',
            'Solve the linear equation: 3x + 7 = 22',
            'Los de lineaire vergelijking op: 3x + 7 = 22',
          ),
          max_points: 10,
          average_score: 9.0,
          success_percent: 90,
        },
        {
          question_number: 2,
          question_text: l(
            '360 sayısının asal çarpanlarını bulun',
            'Find the prime factors of 360',
            'Bepaal de priemfactoren van 360',
          ),
          max_points: 10,
          average_score: 8.6,
          success_percent: 86,
        },
        {
          question_number: 3,
          question_text: l(
            'Üçgendeki eksik açıyı hesaplayın',
            'Calculate the missing angle in the triangle',
            'Bereken de ontbrekende hoek in de driehoek',
          ),
          max_points: 15,
          average_score: 12.9,
          success_percent: 86,
        },
        {
          question_number: 4,
          question_text: l(
            'Ortalama hız problemi',
            'Average speed problem',
            'Gemiddelde snelheid',
          ),
          max_points: 20,
          average_score: 12.0,
          success_percent: 60,
        },
        {
          question_number: 5,
          question_text: l('Kesir işlemleri', 'Fraction operations', 'Breukbewerkingen'),
          max_points: 15,
          average_score: 11.0,
          success_percent: 73,
        },
        {
          question_number: 6,
          question_text: l(
            'Grafik yorumlama ve ortanca',
            'Chart interpretation and median',
            'Grafiekinterpretatie en mediaan',
          ),
          max_points: 15,
          average_score: 12.0,
          success_percent: 80,
        },
        {
          question_number: 7,
          question_text: l(
            'Eşitsizlik çözümü',
            'Solving inequalities',
            'Ongelijkheden oplossen',
          ),
          max_points: 15,
          average_score: 13.4,
          success_percent: 89,
        },
      ],
      analysis: {
        successful_topics: [
          {
            topic: l('Doğrusal Denklemler', 'Linear Equations', 'Lineaire vergelijkingen'),
            avg_success_percent: 90,
            evidence: l(
              'Öğrenciler güçlü cebirsel işlem becerisi gösterdi',
              'Students demonstrated strong algebraic manipulation skills',
              'Leerlingen lieten sterke algebraïsche vaardigheden zien',
            ),
          },
          {
            topic: l('Sayı Özellikleri', 'Number Properties', 'Getaleigenschappen'),
            avg_success_percent: 86,
            evidence: l(
              'Asal çarpanlara ayırmada iyi anlayış',
              'Good understanding of prime factorisation',
              'Goed begrip van priemontbinding',
            ),
          },
          {
            topic: l('Temel Geometri', 'Basic Geometry', 'Basisgeometrie'),
            avg_success_percent: 86,
            evidence: l(
              'Açı hesaplamalarında tutarlı performans',
              'Consistent performance in angle calculations',
              'Consistente prestaties bij hoekberekeningen',
            ),
          },
        ],
        weak_topics: [
          {
            topic: l('Sözel Problemler', 'Word Problems', 'Verhaalsommen'),
            avg_success_percent: 60,
            gap: l(
              'Öğrenciler sözel ifadeleri denkleme çevirmekte zorlanıyorlar',
              'Students struggle to translate word problems into equations',
              'Leerlingen hebben moeite om verhaalsommen naar vergelijkingen te vertalen',
            ),
          },
          {
            topic: l('Rasyonel Sayılar', 'Rational Numbers', 'Rationele getallen'),
            avg_success_percent: 73,
            gap: l(
              'Kesir işlemleri ve ondalık çevirmelerde yaygın hatalar',
              'Common errors in fraction operations and decimal conversions',
              'Veelvoorkomende fouten bij breuken en decimale omzettingen',
            ),
          },
        ],
        class_recommendation: l(
          'Önümüzdeki iki hafta basamaklı çalışma kâğıtları ile sözel problem stratejilerine odaklanın. Rasyonel sayı pratiği için akran öğretimi eşlemesi düşünün.',
          'Focus on word-problem strategies with scaffolded worksheets over the next two weeks. Consider peer-tutoring pairs for rational-number practice.',
          'Focus de komende twee weken op strategieën voor verhaalsommen met opbouwende werkbladen. Overweeg peer-tutoring voor oefening met rationele getallen.',
        ),
      },
      analysis_source: 'llm',
      difficulty: {
        category: 'Moderate',
        score: 55,
        description: l('Orta zorluk', 'Moderate difficulty', 'Gemiddelde moeilijkheid'),
      },
    },
    2: {
      run_id: 2,
      paper_count: 2,
      class_average: { average_score: 66, max_score: 80, success_percent: 82.5 },
      feedback_language: l('Turkish', 'English', 'Dutch'),
      question_stats: [
        {
          question_number: 1,
          question_text: l("Newton ikinci yasa", "Newton's second law", 'Tweede wet van Newton'),
          max_points: 20,
          average_score: 18,
          success_percent: 90,
        },
        {
          question_number: 2,
          question_text: l(
            'Kinematik grafik analizi',
            'Kinematic graph analysis',
            'Kinematische grafiekanalyse',
          ),
          max_points: 20,
          average_score: 16,
          success_percent: 80,
        },
        {
          question_number: 3,
          question_text: l(
            'Dalga boyu-frekans ilişkisi',
            'Wavelength–frequency relationship',
            'Verband golflengte-frequentie',
          ),
          max_points: 20,
          average_score: 15,
          success_percent: 75,
        },
        {
          question_number: 4,
          question_text: l(
            'Enerji koruması problemi',
            'Conservation of energy problem',
            'Vraagstuk behoud van energie',
          ),
          max_points: 20,
          average_score: 17,
          success_percent: 85,
        },
      ],
      analysis: {
        successful_topics: [
          {
            topic: l('Newton Yasaları', "Newton's Laws", 'Wetten van Newton'),
            avg_success_percent: 90,
            evidence: l(
              'Her iki öğrenci de iyi tanımlama ve örnek vermiş',
              'Both students gave good definitions and examples',
              'Beide leerlingen gaven goede definities en voorbeelden',
            ),
          },
          {
            topic: l('Enerji Koruması', 'Conservation of Energy', 'Behoud van energie'),
            avg_success_percent: 85,
            evidence: l(
              'Problem çözümünde doğru uygulama',
              'Correct application in problem solving',
              'Correcte toepassing bij het oplossen',
            ),
          },
        ],
        weak_topics: [
          {
            topic: l('Dalga Hareketi', 'Wave Motion', 'Golfbeweging'),
            avg_success_percent: 75,
            gap: l(
              'Formül kullanımı ve derinlik eksikliği',
              'Weak formula use and lack of depth',
              'Zwak formulegebruik en gebrek aan diepgang',
            ),
          },
        ],
        class_recommendation: l(
          'Dalga konuları için laboratuvar uygulamaları ve görsel materyaller ile desteklenme önerilir.',
          'Lab activities and visual materials are recommended to support the wave topics.',
          'Labo-activiteiten en visueel materiaal worden aanbevolen ter ondersteuning van de golfonderwerpen.',
        ),
      },
      analysis_source: 'llm',
      difficulty: {
        category: 'Moderate',
        score: 50,
        description: l('Orta zorluk', 'Moderate difficulty', 'Gemiddelde moeilijkheid'),
      },
    },
  },

  // ── Members ───────────────────────────────────────────────────
  members: [
    {
      id: 2,
      full_name: l('Ayşe Yılmaz', 'Sarah Mitchell', 'Sanne Jansen'),
      email: l('ayse@yildizkoleji.edu.tr', 'sarah@northfield.edu', 'sanne@sintlucas.nl'),
      role: 'ORG_MANAGER',
      assigned_class_count: 4,
      assigned_class_ids: [1, 2, 3, 6],
    },
    {
      id: 3,
      full_name: l('Mehmet Demir', 'Daniel Foster', 'Daan Bakker'),
      email: l('mehmet@yildizkoleji.edu.tr', 'daniel@northfield.edu', 'daan@sintlucas.nl'),
      role: 'TEACHER',
      assigned_class_count: 2,
      assigned_class_ids: [3, 4],
    },
    {
      id: 4,
      full_name: l('Elif Kaya', 'Rachel Green', 'Femke Vos'),
      email: l('elif@yildizkoleji.edu.tr', 'rachel@northfield.edu', 'femke@sintlucas.nl'),
      role: 'TEACHER',
      assigned_class_count: 1,
      assigned_class_ids: [5],
    },
    {
      id: 5,
      full_name: l('Ahmet Çelik', 'Mark Evans', 'Pieter Bakker'),
      email: l('ahmet@yildizkoleji.edu.tr', 'mark@northfield.edu', 'pieter@sintlucas.nl'),
      role: 'TEACHER',
      assigned_class_count: 3,
      assigned_class_ids: [1, 2, 6],
    },
  ],

  // ── Cost data ─────────────────────────────────────────────────
  wallet: {
    subscriber_id: 1,
    read_balance: 8.75,
  },

  transactions: [
    {
      id: 1,
      credit_amount: 10,
      type: 'TOPUP',
      monetary_amount: 1.43,
      created_by_user_id: 2,
      created_at: '2026-02-01T09:00:00',
    },
    {
      id: 2,
      credit_amount: -0.85,
      type: 'AI_SPEND',
      compute_cost: 0.12,
      created_by_user_id: null,
      created_at: '2026-02-15T12:15:00',
      exam_name: l('Ara Sınav — Cebir', 'Midterm — Algebra', 'Proefwerk — Algebra'),
    },
    {
      id: 3,
      credit_amount: -0.4,
      type: 'AI_SPEND',
      compute_cost: 0.06,
      created_by_user_id: null,
      created_at: '2026-02-20T14:10:00',
      exam_name: l('Dönemlik Fizik Sınavı', 'Term Physics Exam', 'Proefwerk Natuurkunde'),
    },
  ],

  costRows: [
    {
      session_id: 1,
      session_name: l('Ara Sınav — Cebir', 'Midterm — Algebra', 'Proefwerk — Algebra'),
      provider: 'OpenAI',
      model: 'gpt-4o',
      calls: 7,
      input_tokens: 142000,
      output_tokens: 89000,
      cost_usd: 0.97,
    },
    {
      session_id: 2,
      session_name: l('Dönemlik Fizik Sınavı', 'Term Physics Exam', 'Proefwerk Natuurkunde'),
      provider: 'Gemini',
      model: 'gemini-2.0-flash',
      calls: 2,
      input_tokens: 98000,
      output_tokens: 67000,
      cost_usd: 0.78,
    },
  ],
}

// ── Resolver ────────────────────────────────────────────────────

function isDemoText(value: unknown): value is DemoText {
  if (typeof value !== 'object' || value === null) return false
  const v = value as Record<string, unknown>
  return typeof v.tr === 'string' && typeof v.en === 'string' && typeof v.nl === 'string'
}

function localize(value: unknown, lang: Language): unknown {
  if (isDemoText(value)) return value[lang]
  if (Array.isArray(value)) return value.map((item) => localize(item, lang))
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {}
    for (const [key, item] of Object.entries(value)) out[key] = localize(item, lang)
    return out
  }
  return value
}

/** Build the full demo dataset for a UI language. */
export function buildDemoData(lang: Language): DemoData {
  return localize(content, lang) as DemoData
}
